import { useCallback, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import { Loader2 } from 'lucide-react';

import {
    getLocationOptions,
    getPlaceId,
    ILookupSuburbOrPostcodeResponse,
} from '@/apiCalls/google';
import { Input } from '@/components/Input';
import { MapComponent } from '@/components/MapComponent';
import {
    EState,
    selectSellerSelectedStoreId,
    setSellerSelectedStoreId,
} from '@/context/borrower';
import { selectPartner } from '@/context/display';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import { calculateDistance } from '@/utils/calculateDistance';
import { capitalizeString } from '@/utils/capitalizeString';

export interface IProps {
    baseId: string;
    showErrors: boolean;
}

export const testId = 'StoreInput';

// Base store type (matches your existing store structure)
interface BaseStoreOption {
    name: string;
    postCode: string;
    state: EState;
    partnerId: string;
    coordinates: { lat: number; lng: number };
}

// Extended type that includes distance
interface StoreOptionWithDistance extends BaseStoreOption {
    distance: number;
}

export const StoreInput = ({ showErrors, baseId }: IProps): JSX.Element => {
    // ** Redux **
    const currentState = useAppSelector(selectSellerSelectedStoreId);
    const dispatch = useAppDispatch();
    const partner = useAppSelector(selectPartner);
    const [loading, setLoading] = useState(false);
    const { storeSelectorOptions, storeSelectorId } = partner;

    // Find the current store for this selector
    const currentStoreObject = currentState?.find(
        (store) => store.storeSelectorId === storeSelectorId
    );

    // Don't set a default store, allow null/undefined for unselected state
    const initialActiveIndex = currentStoreObject
        ? storeSelectorOptions?.findIndex(
              (option) => option.partnerId === currentStoreObject?.storeId
          ) ?? -1
        : -1;

    const [activeStore, setActiveStore] = useState<number | null>(
        initialActiveIndex >= 0 ? initialActiveIndex : null
    );
    const [search, setSearch] = useState<string>('');

    // Change this line
    const [filterOptions, setFilterOptions] = useState<BaseStoreOption[]>(
        storeSelectorOptions ?? []
    );

    // Get initial coordinates from active store if available
    const initialMapCenter = storeSelectorOptions?.[initialActiveIndex]
        ?.coordinates ?? {
        lat: -37.8179977,
        lng: 144.9503271,
    };

    // Add state for map center
    const [mapCenter, setMapCenter] = useState(initialMapCenter);

    const [searchOptions, setSearchOptions] = useState<
        ILookupSuburbOrPostcodeResponse['predictions']
    >([]);
    const [showSearchOptions, setShowSearchOptions] = useState(false);

    // Add ref for the search container
    const searchContainerRef = useRef<HTMLDivElement>(null);

    // Add useEffect for click outside handling
    useEffect(() => {
        function handleClickOutside(event: MouseEvent): void {
            if (
                searchContainerRef.current &&
                !searchContainerRef.current.contains(event.target as Node)
            ) {
                setShowSearchOptions(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Create a debounced version of the API call
    const debouncedLocationSearch = useCallback(
        debounce(async (searchTerm: string) => {
            try {
                const response = await getLocationOptions(searchTerm);
                setSearchOptions(response.predictions);
                setShowSearchOptions(true);
            } catch (error) {
                console.error('Error fetching location options:', error);
            }
        }, 300),
        []
    );

    const handleSearch = async (e: string): Promise<void> => {
        setSearch(e);

        // Only fetch location options if search is 3 or more characters
        if (e.length >= 3) {
            debouncedLocationSearch(e);
        } else {
            setSearchOptions([]);
            setShowSearchOptions(false);
        }
    };

    const handleLocationSelect = async (
        description: string,
        placeId: string
    ): Promise<void> => {
        setLoading(true);
        try {
            // Get coordinates from the selected place
            const response = await getPlaceId(placeId);
            const location = response.result.geometry.location;

            // Calculate distances and sort stores
            if (storeSelectorOptions) {
                // Get stores with distances calculated and sorted
                const storesWithDistance = calculateDistances(
                    location,
                    storeSelectorOptions
                );

                // Update filterOptions to show sorted stores with distances
                setFilterOptions(storesWithDistance);

                // First store will be the closest after sorting
                const closestStore = storesWithDistance[0];

                // Find index of closest store in original storeSelectorOptions
                const closestStoreIndex = storeSelectorOptions.findIndex(
                    (store) => store.partnerId === closestStore.partnerId
                );

                // Update UI
                setActiveStore(0); // Set to 0 since we're showing the sorted list
                setMapCenter(closestStore.coordinates);

                // Update Redux store with closest store
                if (storeSelectorId) {
                    dispatch(
                        setSellerSelectedStoreId([
                            {
                                storeId: closestStore.partnerId,
                                storeSelectorId: storeSelectorId,
                            },
                        ])
                    );
                }
            }
        } catch (error) {
            console.error('Error fetching place details:', error);
        } finally {
            setSearch(description);
            setShowSearchOptions(false);
            setLoading(false);
        }
    };

    // Update handleStoreClick to work with sorted stores
    function handleStoreClick(i: number): void {
        setActiveStore(i);
        if (!filterOptions) return;

        const selectedStore = filterOptions[i];
        if (selectedStore.coordinates) {
            setMapCenter(selectedStore.coordinates);
        }

        const newStoreObject = {
            storeId: selectedStore.partnerId,
            storeSelectorId,
        };

        const currentSelectedStores = currentState || [];
        const existingIndex = currentSelectedStores.findIndex(
            (store) => store.storeSelectorId === storeSelectorId
        );

        let updatedStores;
        if (existingIndex === -1) {
            updatedStores = [...currentSelectedStores, newStoreObject];
        } else {
            updatedStores = currentSelectedStores.map((store, index) =>
                index === existingIndex ? newStoreObject : store
            );
        }

        dispatch(setSellerSelectedStoreId(updatedStores));
    }

    // Update calculateDistances to use these types
    const calculateDistances = (
        userLocation: { lat: number; lng: number },
        stores: BaseStoreOption[] | undefined
    ): (BaseStoreOption | StoreOptionWithDistance)[] => {
        if (!stores) return [];
        return stores
            .map((store) => ({
                ...store,
                distance: calculateDistance(
                    userLocation.lat,
                    userLocation.lng,
                    store.coordinates.lat,
                    store.coordinates.lng
                ),
            }))
            .sort(
                (a, b) =>
                    (a as StoreOptionWithDistance).distance -
                    (b as StoreOptionWithDistance).distance
            );
    };

    // Updated getCurrentLocation function
    const handleGetCurrentLocation = (): void => {
        setLoading(true);
        setSearch('');
        setShowSearchOptions(false);

        if (!navigator.geolocation) {
            console.log('Geolocation is not supported by your browser');
            setLoading(false);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const userLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                };

                // Calculate distances and sort stores
                if (storeSelectorOptions) {
                    const storesWithDistance = calculateDistances(
                        userLocation,
                        storeSelectorOptions
                    );
                    setFilterOptions(storesWithDistance);

                    const closestStore = storesWithDistance[0];
                    setActiveStore(0);
                    setMapCenter(closestStore.coordinates);

                    if (storeSelectorId) {
                        dispatch(
                            setSellerSelectedStoreId([
                                {
                                    storeId: closestStore.partnerId,
                                    storeSelectorId: storeSelectorId,
                                },
                            ])
                        );
                    }
                }
                setLoading(false);
            },
            (error) => {
                console.log('Error getting location:', error);
                setLoading(false);
            }
        );
    };

    // Add a type guard function
    const hasDistance = (
        store: BaseStoreOption | StoreOptionWithDistance
    ): store is StoreOptionWithDistance => {
        return 'distance' in store;
    };

    return (
        <div data-testid={testId} className='flex flex-col py-4 relative'>
            {loading && (
                <div className='absolute inset-0 bg-white/80 flex items-center justify-center z-50 rounded-lg'>
                    <Loader2 className='h-40 w-40 animate-spin' />
                </div>
            )}
            <div className='relative'>
                <MapComponent mapCenter={mapCenter} />
            </div>
            <div className='flex gap-2'>
                <div className='relative flex-1' ref={searchContainerRef}>
                    <Input
                        placeholder='Suburb or postcode'
                        id={`${baseId}-input-store`}
                        value={search}
                        onChange={handleSearch}
                        showErrors={activeStore === null && showErrors}
                    />
                    {showSearchOptions && searchOptions.length > 0 && (
                        <div className='absolute z-10 w-full bg-white mt-1 rounded-md shadow-lg border border-gray-200'>
                            {searchOptions.map((option) => (
                                <div
                                    key={option.place_id}
                                    className='px-4 py-2 hover:bg-gray-100 cursor-pointer'
                                    onClick={(): Promise<void> =>
                                        handleLocationSelect(
                                            option.description,
                                            option.place_id
                                        )
                                    }
                                >
                                    {option.description}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                {/* Note: This works to find the user's location but I don't have time to add the distance to the store to make it useful */}
                <div className='flex items-center justify-center py-2'>
                    <img
                        src='/mapicon.svg'
                        className={`h-8 w-8 cursor-pointer text-primary`}
                        onClick={handleGetCurrentLocation}
                    />
                </div>
            </div>
            <h1 className={`fieldHeading text-lg pt-3`}>Choose Your Store</h1>
            {filterOptions.map((option, i) => (
                <div
                    key={i}
                    onClick={(): void => handleStoreClick(i)}
                    className={`${
                        activeStore === i &&
                        'bg-[color-mix(in_srgb,var(--primaryColor)_10%,transparent)] rounded-xl'
                    } px-2 py-1 `}
                >
                    <p className='text-lg'>
                        {i + 1}. {capitalizeString(option.name)}
                        {hasDistance(option) && (
                            <span className='text-sm text-gray-600 ml-2'>
                                ({option.distance} km away)
                            </span>
                        )}
                    </p>
                    <div className='flex'>
                        <div className='w-[20px] h-1' />
                        <p className='text-sm'>
                            {option.postCode} {option.state}
                        </p>
                    </div>
                </div>
            ))}
        </div>
    );
};
